import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import './AdminCourses.css';

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [groupedCourses, setGroupedCourses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedAccounts, setExpandedAccounts] = useState({});

  useEffect(() => {
    loadAllCourses();
  }, []);

  const loadAllCourses = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in.');
        return;
      }

      const response = await axios.get('/api/components/admin/all-courses', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter out Draft status courses
      const filteredCourses = (response.data.courses || []).filter(course => course.status !== 'Draft');
      setCourses(filteredCourses);
      
      // Group courses by account
      const grouped = groupCoursesByAccount(filteredCourses);
      setGroupedCourses(grouped);
      
      // Initialize all accounts as collapsed by default
      const initialExpanded = {};
      Object.keys(grouped).forEach(accountId => {
        initialExpanded[accountId] = false;
      });
      setExpandedAccounts(initialExpanded);
    } catch (error) {
      console.error('Error loading all courses:', error);
      setError(`Error loading courses: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAccountExpansion = (accountId) => {
    console.log('Toggling account:', accountId, 'Current state:', expandedAccounts[accountId]);
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const groupCoursesByAccount = (courses) => {
    const grouped = {};
    
    courses.forEach(course => {
      const accountKey = course.account_id || 'unknown';
      const accountName = course.first_name && course.last_name 
        ? `${course.first_name} ${course.last_name}` 
        : 'Unknown User';
      const accountEmail = course.primary_email || 'No email';
      
      if (!grouped[accountKey]) {
        grouped[accountKey] = {
          accountInfo: {
            account_id: course.account_id,
            name: accountName,
            email: accountEmail,
            first_name: course.first_name,
            last_name: course.last_name
          },
          courses: []
        };
      }
      
      grouped[accountKey].courses.push(course);
    });
    
    return grouped;
  };

  const handleViewCourse = (courseId) => {
    window.open(`/course-viewer?id=${courseId}&command=preview`, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <div className="admin-courses-container">
      <div className="admin-courses-content">
        <div className="admin-courses-header">
          <div className="header-left">
            <h1>LMS Courses</h1>
            <p className="header-subtitle">Manage all courses in the system</p>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="loading-container">
            <LoadingSpinner />
            <span>Loading all courses...</span>
          </div>
        ) : (
          <>
            {/* Courses Count */}
            <div className="courses-count">
              <div className="count-item">
                <span className="count-number">{Object.keys(groupedCourses).length}</span>
                <span className="count-label">Accounts</span>
              </div>
              <div className="count-item">
                <span className="count-number">{courses.length}</span>
                <span className="count-label">Total Courses</span>
              </div>
            </div>

            {/* Creators Grid */}
            {courses.length > 0 ? (
              <div className="creators-grid">
                {Object.values(groupedCourses).map((accountGroup, index) => (
                  <div 
                    key={accountGroup.accountInfo.account_id || index} 
                    className={`creator-card ${expandedAccounts[accountGroup.accountInfo.account_id] ? 'expanded' : ''}`}
                  >
                    <div 
                      className="creator-header"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleAccountExpansion(accountGroup.accountInfo.account_id);
                      }}
                    >
                      <div className="creator-info">
                        <h3 className="creator-name">{accountGroup.accountInfo.name}</h3>
                        <p className="creator-email">{accountGroup.accountInfo.email}</p>
                        <div className="creator-course-count">{accountGroup.courses.length} course{accountGroup.courses.length !== 1 ? 's' : ''}</div>
                      </div>
                      <button 
                        className="expand-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleAccountExpansion(accountGroup.accountInfo.account_id);
                        }}
                      >
                        {expandedAccounts[accountGroup.accountInfo.account_id] ? '▼' : '▶'}
                      </button>
                    </div>
                    
                    {expandedAccounts[accountGroup.accountInfo.account_id] && (
                      <div className="courses-grid">
                        {accountGroup.courses.map((course) => (
                          <div 
                            key={course.id} 
                            className="course-card"
                            onClick={() => handleViewCourse(course.id)}
                            style={{ cursor: 'pointer' }}
                            title="Click to view course"
                          >
                            <div className="course-header">
                              <h4 className="course-title">
                                {course.draft?.courseTitle || course.title || 'Untitled Course'}
                              </h4>
                              <div className="course-dates">
                                <span className="date-label">Created: {formatDate(course.createddate)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-courses">
                <div className="no-courses-icon">📚</div>
                <h3>No Courses Found</h3>
                <p>There are no courses in the system yet.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCourses;
